import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import {
  type PaymentMethod,
  type StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import { capitalizeFirstLetter, type IconName } from '@sg/garnish';

import { type UseStripePaymentProps } from '../../types';
import { fallbackNoPaymentIsNeeded } from '../../utils';
import {
  getCreditCardIcon,
  removeEndingIn,
  useLabelAndIconByType,
  useNotifyStripeError,
} from './helpers';

export const useStripePayment = (props: UseStripePaymentProps) => {
  const { total, paymentMethods, trackStripeUsage, onConfirm } = props;

  const notifyRef = useRef(useNotifyStripeError(trackStripeUsage));

  // ─── Stripe ────────────────────────────────────────────────────────────────

  const stripe = useStripe();
  const elements = useElements();

  // ─── Utils ─────────────────────────────────────────────────────────────────

  const convertTypeToLabelAndIcon = useLabelAndIconByType();

  // ─── State ─────────────────────────────────────────────────────────────────

  const [createdPaymentMethod, setCreatedPaymentMethod] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedPaymentIcon, setSelectedPaymentIcon] =
    useState<IconName>('IconCreditCard');

  // ─── Payment Method Creation ───────────────────────────────────────────────

  const createPaymentMethod = useCallback(async () => {
    try {
      if (!stripe || !elements) {
        notifyRef.current();
        return;
      }

      const { error: submitError } = await elements.submit();

      if (submitError) {
        notifyRef.current(submitError.message);
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      if (error) {
        notifyRef.current(error.message);
        return;
      }

      setCreatedPaymentMethod(paymentMethod.id);

      return paymentMethod;
    } catch (error) {
      notifyRef.current((error as Error).message);
    }
  }, [elements, stripe]);

  // ─── Handle Payment Change ─────────────────────────────────────────────────

  const handlePaymentElementChange = useMemo(
    () =>
      getPaymentMethodChangeHandler({
        paymentMethods,
        convertTypeToLabelAndIcon,
        setCreatedPaymentMethod,
        setSelectedPaymentMethod,
        setSelectedPaymentIcon,
        createPaymentMethod,
      }),
    [convertTypeToLabelAndIcon, createPaymentMethod, paymentMethods],
  );

  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }

    const paymentElement = elements.getElement('payment');

    if (!paymentElement) {
      return;
    }

    paymentElement.on('change', handlePaymentElementChange);

    return () => {
      paymentElement.off('change', handlePaymentElementChange);
    };
  }, [elements, stripe, handlePaymentElementChange]);

  // ─── Confirm Callback ──────────────────────────────────────────────────────

  const confirm = useCallback(async () => {
    // Use first saved payment method if no payment method is selected from the SDK.
    if (!selectedPaymentMethod && paymentMethods.length > 0) {
      trackStripeUsage({ event: 'confirming_with_fallback' });
      onConfirm(paymentMethods[0].id);

      return;
    }

    if (total === 0) {
      trackStripeUsage({ event: 'confirming_with_zero_total' });
      onConfirm(fallbackNoPaymentIsNeeded(paymentMethods));

      return;
    }

    if (createdPaymentMethod) {
      trackStripeUsage({ event: 'confirming_with_previous_payment_method' });
      onConfirm(createdPaymentMethod);

      return;
    }

    const paymentMethod = await createPaymentMethod();

    if (!paymentMethod) return;

    trackStripeUsage({ event: 'confirming_with_created_payment_method' });
    onConfirm(paymentMethod.id);
  }, [
    selectedPaymentMethod,
    paymentMethods,
    total,
    createdPaymentMethod,
    createPaymentMethod,
    trackStripeUsage,
    onConfirm,
  ]);

  return {
    isGooglePay: selectedPaymentMethod === GOOGLE_PAY,
    selectedPaymentMethod,
    selectedPaymentIcon,
    confirm,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Handles changes to the payment method, setting state as needed.
 */
const getPaymentMethodChangeHandler = (params: PaymentMethodChangeHandler) => {
  const {
    paymentMethods,
    convertTypeToLabelAndIcon,
    setCreatedPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedPaymentIcon,
    createPaymentMethod,
  } = params;

  return async (event: StripePaymentElementChangeEvent) => {
    setCreatedPaymentMethod('');

    const id = event.value.payment_method?.id;
    const savedPaymentMethod = paymentMethods.find((pm) => pm.id === id);

    // Customer selected a card that is already saved in Stripe.
    if (savedPaymentMethod?.description && savedPaymentMethod?.cardType) {
      setSelectedPaymentMethod(removeEndingIn(savedPaymentMethod.description));
      setSelectedPaymentIcon(getCreditCardIcon(savedPaymentMethod.cardType));
      return;
    }

    // Customer is adding a new card.
    if (event.value.type === 'card' && event.complete) {
      const paymentMethod = await createPaymentMethod();

      if (!paymentMethod?.card) return;

      const { brand, last4 } = paymentMethod.card;

      const label = `${capitalizeFirstLetter(brand)} ${last4}`;

      setSelectedPaymentMethod(label);
      setSelectedPaymentIcon(getCreditCardIcon(brand.toUpperCase()));

      return;
    }

    // Customer selected a wallet payment type.
    if (event.value.type) {
      const { label, icon } = convertTypeToLabelAndIcon(event.value.type);
      setSelectedPaymentMethod(label);
      setSelectedPaymentIcon(icon);
    }
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type PaymentMethodChangeHandler = {
  paymentMethods: UseStripePaymentProps['paymentMethods'];
  convertTypeToLabelAndIcon: ReturnType<typeof useLabelAndIconByType>;
  setCreatedPaymentMethod: (value: string) => void;
  setSelectedPaymentMethod: (value: string) => void;
  setSelectedPaymentIcon: (value: IconName) => void;
  createPaymentMethod: () => Promise<PaymentMethod | undefined>;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const GOOGLE_PAY = 'Google Pay';
